import { fncClassName } from "@/lib/utils/fncClassName";
import type { Node } from "./types";

export const financialsTreeDataFinTemplate: Node[] = [
	{
		name: "Financial Metrics",
		children: [
			{
				name: "Valuation",
				children: [
					{
						field: "calculations.marketcap",
						name: "Market Capitalization",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "calculations.enterprisevalue",
						name: "Enterprise Value",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{ field: "calculations.pricetobook", name: "Price to Book" },
					{
						field: "calculations.earningsyield",
						name: "Earnings Yiel",
						options: { style: "percent" },
					},
					{
						field: "calculations.dividendyield",
						name: "Dividend Yield",
						options: { style: "percent" },
					},
					{ field: "calculations.pricetoearnings", name: "Price to Earnings" },
					{
						field: "calculations.pricetotangiblebook",
						name: "Price to Tangible Book Value",
					},
					{ field: "calculations.pricetorevenue", name: "Price to Revenue" },
					{
						field: "calculations.evtoinvestedcapital",
						name: "EV to Invested Capital",
					},
					{ field: "calculations.evtorevenue", name: "EV to Revenue" },
					{ field: "calculations.evtoebit", name: "EV to EBIT" },
					{ field: "calculations.evtonopat", name: "EV to NOPAT" },
					{ field: "calculations.evtoebitda", name: "EV to EBITDA " },
					{ field: "calculations.evtoocf", name: "EV to Operating Cash Flow" },
					{
						field: "calculations.evtofcff",
						name: "EV to Free Cash Flow to Firm",
					},
				],
			},
			{
				name: "Growth Rates",
				children: [
					{
						field: "calculations.revenuegrowth",
						name: "Revenue Y/Y",
						options: { style: "percent" },
						fncClassName,
					},
					{
						field: "calculations.revenueqoqgrowth",
						name: "Revenue Q/Q Growth",
						options: { style: "percent" },
						fncClassName,
					},
					{
						field: "calculations.ebitdagrowth",
						name: "EBITDA Y/Y",
						options: { style: "percent" },
						fncClassName,
					},
					{
						field: "calculations.ebitdaqoqgrowth",
						name: "EBITDA Q/Q Growth",
						options: { style: "percent" },
						fncClassName,
					},
					{
						field: "calculations.nopatgrowth",
						name: "NOPAT Y/Y",
						options: { style: "percent" },
						fncClassName,
					},
					{
						field: "calculations.nopatqoqgrowth",
						name: "NOPAT Q/Q",
						options: { style: "percent" },
						fncClassName,
					},
					{
						field: "calculations.ebitgrowth",
						name: "EBIT Y/Y",
						options: { style: "percent" },
						fncClassName,
					},
					{
						field: "calculations.ebitqoqgrowth",
						name: "EBIT Q/O",
						options: { style: "percent" },
						fncClassName,
					},
					{
						field: "calculations.netincomegrowth",
						name: "Net Income Y/Y",
						options: { style: "percent" },
						fncClassName,
					},
					{
						field: "calculations.netincomeqoqgrowth",
						name: "Net Income Q/Q",
						options: { style: "percent" },
						fncClassName,
					},
					{
						field: "calculations.epsgrowth",
						name: "EPS Y/Y",
						options: { style: "percent" },
						fncClassName,
					},
					{
						field: "calculations.epsqoqgrowth",
						name: "EPS Q/Q",
						options: { style: "percent" },
						fncClassName,
					},
					{
						field: "calculations.ocfgrowth",
						name: "Operating Cash Flow Y/Y",
						options: { style: "percent" },
						fncClassName,
					},
					{
						field: "calculations.ocfqoqgrowth",
						name: "Operating Cash Flow Q/Q",
						options: { style: "percent" },
						fncClassName,
					},
					{
						field: "calculations.investedcapitalgrowth",
						name: "Invested Capital Y/Y",
						options: { style: "percent" },
						fncClassName,
					},

					{
						field: "calculations.investedcapitalqoqgrowth",
						name: "Invested Capital Q/Q",
						options: { style: "percent" },
						fncClassName,
					},
				],
			},
			{
				name: "Profitability Margins",
				children: [
					{
						field: "calculations.ebitdamargin",
						name: "EBITDA Margin",
						options: { style: "percent" },
					},
					{
						field: "calculations.nopatmargin",
						name: "NOPAT Margin",
						options: { style: "percent" },
					},
					{
						field: "calculations.sgaextorevenue",
						name: "SG&A Expenses to Revenue",
						options: { style: "percent" },
					},
					{
						field: "calculations.opextorevenue",
						name: "Operating Expenses to Revenue",
						options: { style: "percent" },
					},
					{
						field: "calculations.efftaxrate",
						name: "Effective Tax Rate",
						options: { style: "percent" },
					},
				],
			},
			{
				name: "Metrics",
				children: [
					{
						field: "calculations.nopat",
						name: "Net Operating Profit After Taxes (NOPAT)",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "calculations.ebitda",
						name: "EBITDA",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "calculations.ebit",
						name: "EBIT",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "calculations.freecashflow",
						name: "Free Cash Flow",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "calculations.investedcapital",
						name: "Invested Capital",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "calculations.netnonopobligations",
						name: "Net Nonoperating Obligations",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
						fncClassName,
					},
					{
						field: "calculations.debt",
						name: "Total Debt",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "balance_sheet_statement.longtermdebt",
						name: "Long-Term Debt ",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "calculations.netdebt",
						name: "Net Debt",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
						fncClassName,
					},
					{
						field: "calculations.totalcapital",
						name: "Total Capital",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "calculations.bookvaluepershare",
						name: "Book Value per Share",
						options: { style: "currency", currency: "USD" },
					},
					{
						field: "calculations.tangbookvaluepershare",
						name: "Tangible Book Value per Share",
						options: { style: "currency", currency: "USD" },
					},
					{
						field: "calculations.investedcapitalincreasedecrease",
						name: "Increase i (Decrease) in Invested Capital",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
						fncClassName,
					},
					{
						field: "calculations.capex",
						name: "Capital Expenditures (CapEx)",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
				],
			},
			{
				name: "FOIC / ROE Disaggregation",
				children: [
					{
						field: "calculations.normalizednopatmargin",
						name: "Net Operating Profit After Tax Margin (NOPAT N",
						options: { style: "percent" },
					},
					{
						field: "calculations.investedcapitalturnover",
						name: "Invested Capital Turnover",
						fncClassName,
					},
					{
						field: "calculations.roic",
						name: "Return on Invested Capital (ROIC)",
						options: { style: "percent" },
						fncClassName,
					},
					{
						field: "calculations.finleverage",
						name: "Financial Leverage",
						fncClassName,
					},
					{
						field: "calculations.nnep",
						name: "Net Nonoperating Expense Percent",
						options: { style: "percent" },
					},
					{
						field: "calculations.roicnnepspread",
						name: "ROIC-NNEP Spread",
						options: { style: "percent" },
						fncClassName,
					},
					{
						field: "calculations.rnnoa",
						name: "Return on Net Nonoperating Assets (RNNOA)",
						options: { style: "percent" },
					},
					{
						field: "calculations.roe",
						name: "Return on Equity (ROE)",
						options: { style: "percent" },
					},
					{
						field: "calculations.profitmargin",
						name: "Profit Margin",
						options: { style: "percent" },
					},
					{
						field: "calculations.taxburdenpct",
						name: "ax Burden Percent",
						options: { style: "percent" },
					},
					{
						field: "calculations.interestburdenpct",
						name: "Interest Burden Percent",
						options: { style: "percent" },
					},
					{
						field: "calculations.ebitmargin",
						name: "EBIT Margin",
						options: { style: "percent" },
					},
					{ field: "calculations.assetturnover", name: "Asset Turnover" },
					{ field: "calculations.leverageratio", name: "Leverage Ratio" },
					{
						field: "calculations.oroa",
						name: "Operating Return on Assets (OROA)",
						options: { style: "percent" },
					},
					{
						field: "calculations.roa",
						name: "Return on Assets (ROA)",
						options: { style: "percent" },
					},
					{
						field: "calculations.compoundleveragefactor",
						name: "Compound Leverage Factor",
					},
					{
						field: "calculations.noncontrollinginterestsharingratio",
						name: "Noncontrolling Interest Sharing Ratio",
						options: { style: "percent" },
					},
					{
						field: "calculations.roce",
						name: "Return on Common Equity (ROCE)",
						options: { style: "percent" },
					},
					{
						field: "calculations.croic",
						name: "Cash Return on Invested Capital (CROIC)",
						options: { style: "percent" },
					},
				],
			},
			{
				name: "Effectiveness",
				children: [
					{
						field: "calculations.divpayoutratio",
						name: "Dividend Payout Ratio",
						options: { style: "percent" },
					},
					{
						field: "calculations.augmentedpayoutratio",
						name: "Augmented Payout Ratio",
						options: { style: "percent" },
					},
					{
						field: "calculations.ocftocapex",
						name: "Operating Cash Flow to CapEx",
						options: { style: "percent" },
					},
				],
			},
			{
				name: "Efficiencies",
				children: [
					{
						field: "calculations.investedcapitalturnover",
						name: "Invested Capital Turnover",
						fncClassName,
					},
					{ field: "calculations.assetturnover", name: "Asset Turnover" },
					{ field: "calculations.faturnover", name: "Fixed Asset Turnover" },
				],
			},
			{
				name: "Leverage",
				children: [
					{ field: "calculations.debttoequity", name: "Debt to Equity" },
					{
						field: "calculations.ltdebttoequity",
						name: "Long-Term Debt to Equity",
					},
				],
			},
			{
				name: "Capital Structure",
				children: [
					{
						field: "calculations.stdebttocap",
						name: "Short-Term Debt to Total Capital",
						options: { style: "percent" },
					},
					{
						field: "calculations.ltdebttocap",
						name: "Long-Term Debt to Total Capital",
						options: { style: "percent" },
					},
					{
						field: "calculations.debttototalcapital",
						name: "Debt to Total Capital",
						options: { style: "percent" },
					},
					{
						field: "calculations.preferredtocap",
						name: "Preferred Equity to Total Capital",
						options: { style: "percent" },
					},
					{
						field: "calculations.noncontrolinttocap",
						name: "Noncontrolling Interests to Total Capital",
						options: { style: "percent" },
					},
					{
						field: "calculations.commontocap",
						name: "Common Equity to Total Capital",
						options: { style: "percent" },
					},
				],
			},
			{
				name: "Solvency",
				children: [
					{
						field: "calculations.debttoebitda",
						name: "Debt to EBITDA",
						fncClassName,
					},
					{
						field: "calculations.netdebttoebitda",
						name: "Net Debt to EBITDA",
						fncClassName,
					},
					{
						field: "calculations.ltdebttoebitda",
						name: "Long-Term Debt to EBITDA",
						fncClassName,
					},
					{
						field: "calculations.debttonopat",
						name: "Debt to NOPAT",
						fncClassName,
					},
					{
						field: "calculations.netdebttonopat",
						name: "Net Debt to NOPAT",
						fncClassName,
					},
					{
						field: "calculations.ltdebttonopat",
						name: "Long-Term Debt to NOPAT",
						fncClassName,
					},
				],
			},
			{
				name: "Coverage",
				children: [
					{
						field: "calculations.ebittointerestex",
						name: "EBIT to Interest Expense",
					},
					{
						field: "calculations.nopattointerestex",
						name: "NOPAT to Interest Expense",
					},
					{
						field: "calculations.fcfftointerestex",
						name: "Free Cash Flow to Firm to Interest Expense",
					},
					{
						field: "calculations.ocftointerestex",
						name: "Operating Cash Flow to Interest Expense",
					},
					{
						field: "calculations.ebitlesscapextointerestex",
						name: "EBIT Less CapEx to Interest Expense",
					},
					{
						field: "calculations.nopatlesscapextointex",
						name: "NOPAT Less CapEx to Interest Expense",
					},
					{
						field: "calculations.ocflesscapextointerestex",
						name: "Operating Cash Flow Less CapEx to Interest Exp",
					},
				],
			},
		],
	},

	{
		name: "Income Statement",
		children: [
			{
				field: "income_statement.totalinterestincome",
				name: "Total Interest Income",
				options: {
					notation: "compact",
					style: "currency",
					currency: "USD",
					maximumFractionDigits: 3,
				},
				children: [
					{
						field: "income_statement.loansandleaseinterestincome",
						name: "Loans and Leases",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "income_statement.investmentsecuritiesinterestincome",
						name: "Investment Securities",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "income_statement.depositsinterestincome",
						name: "Deposits and Money Market Investments",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
				],
			},
			{
				field: "income_statement.totalinterestexpense",
				name: "Total Cost of Revenue",
				options: {
					notation: "compact",
					style: "currency",
					currency: "USD",
					maximumFractionDigits: 3,
				},
				children: [
					{
						field: "income_statement.shorttermborrowinginterestexpense",
						name: "Short-Term Borrowings",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "income_statement.longtermdebtinterestexpense",
						name: "Long-Term Debt",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
				],
			},
			{
				field: "income_statement.netinterestincome",
				name: "Net Interest Income / (Expense)",
				options: {
					notation: "compact",
					style: "currency",
					currency: "USD",
					maximumFractionDigits: 3,
				},
				fncClassName,
			},
			{
				field: "income_statement.totalnoninterestincome",
				name: "Total Non-Interest Income",
				options: {
					notation: "compact",
					style: "currency",
					currency: "USD",
					maximumFractionDigits: 3,
				},
				children: [
					{
						field: "income_statement.otherservicechargeincome",
						name: "Other Service Charges",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "income_statement.netrealizedcapitalgains",
						name: "Net Realized Capital Gains on Investments",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
				],
			},
			{
				field: "income_statement.totalrevenue",
				name: "Total Revenue",
				options: {
					notation: "compact",
					style: "currency",
					currency: "USD",
					maximumFractionDigits: 3,
				},
			},
			{
				field: "income_statement.provisionforcreditlosses",
				name: "Provision for Credit Losses",
				options: {
					notation: "compact",
					style: "currency",
					currency: "USD",
					maximumFractionDigits: 3,
				},
			},
			{
				field: "income_statement.totalnoninterestexpense",
				name: "Total Non-Interest Expense",
				options: {
					notation: "compact",
					style: "currency",
					currency: "USD",
					maximumFractionDigits: 3,
				},
				children: [
					{
						field: "income_statement.salariesandemployeebenefitsexpense",
						name: "Salaries and Employee Benefits",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "income_statement.netoccupancyequipmentexpense",
						name: "Net Occupancy & Equipment Expense",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "income_statement.marketingexpense",
						name: "Marketing Expense",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "income_statement.otheroperatingexpenses",
						name: "Other Operating Expenses",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
				],
			},
			{
				field: "income_statement.totalpretaxincome",
				name: "Total Pre-Tax Income ",
				options: {
					notation: "compact",
					style: "currency",
					currency: "USD",
					maximumFractionDigits: 3,
				},
			},
			{
				field: "income_statement.netincomecontinuing",
				name: "Net Income / (Loss) Continuing Operations",
				options: {
					notation: "compact",
					style: "currency",
					currency: "USD",
					maximumFractionDigits: 3,
				},
			},
			{
				field: "income_statement.netincome",
				name: "Consolidated Net Income / (Loss)",
				options: {
					notation: "compact",
					style: "currency",
					currency: "USD",
					maximumFractionDigits: 3,
				},
			},
			{
				field: "income_statement.preferreddividends",
				name: "Preferred Stock Dividends Declared",
				options: {
					notation: "compact",
					style: "currency",
					currency: "USD",
					maximumFractionDigits: 3,
				},
			},
			{
				field: "income_statement.netincometocommon",
				name: "Net Income / (Loss) Attributable to Common Shareholders",
				options: {
					notation: "compact",
					style: "currency",
					currency: "USD",
					maximumFractionDigits: 3,
				},
			},
			{
				field: "income_statement.weightedavedilutedsharesos",
				name: "Weighted Average Diluted Shares Outstanding",
			},
			{
				field: "income_statement.dilutedeps",
				name: "Diluted Earnings per Share",
				options: { style: "currency", currency: "USD" },
			},
			{
				field: "income_statement.cashdividendspershare",
				name: "Cash Dividends to Common per Share",
				options: { style: "currency", currency: "USD" },
			},
		],
	},

	{
		name: "Balance Sheet",
		children: [
			{
				field: "balance_sheet_statement.netloansandleases",
				name: "Loans and Leases, Net of Allowance",
				options: {
					notation: "compact",
					style: "currency",
					currency: "USD",
					maximumFractionDigits: 3,
				},
				fncClassName,
				children: [
					{
						field: "balance_sheet_statement.cashandequivalents",
						name: "Cash and Due from Banks",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "balance_sheet_statement.restrictedcash",
						name: "Restricted Cash",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "balance_sheet_statement.fedfundssold",
						name: "Federal Funds Sold",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field:
							"balance_sheet_statement.interestbearingdepositsatotherbanks",
						name: "Interest Bearing Deposits at Other Banks",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "balance_sheet_statement.timedepositsplaced",
						name: "Time Deposits Placed and Other Short-Term Investments",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "balance_sheet_statement.loansandleases",
						name: "Loans and Leases",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "balance_sheet_statement.allowanceforloanandleaselosses",
						name: "Allowance for Loan and Lease Losses",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
				],
			},
			{
				field: "balance_sheet_statement.totalassets",
				name: "Total Assets",
				options: {
					notation: "compact",
					style: "currency",
					currency: "USD",
					maximumFractionDigits: 3,
				},
				children: [
					{
						field: "balance_sheet_statement.loansheldforsale",
						name: "Loans Held for Sale",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "balance_sheet_statement.accruedinvestmentincome",
						name: "Accrued Investment Income",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "balance_sheet_statement.netpremisesandequipment",
						name: "Premises and Equipment, Net",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "balance_sheet_statement.mortgageservicingrights",
						name: "Mortgage Servicing Rights",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "balance_sheet_statement.unearnedpremiumsdebit",
						name: "Unearned Premiums",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "balance_sheet_statement.deferredacquisitioncost",
						name: "Deferred Acquisition Cost",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "balance_sheet_statement.goodwill",
						name: "Goodwill",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "balance_sheet_statement.intangibleassets",
						name: "Intangible Assets",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "balance_sheet_statement.otherassets",
						name: "Other Assets",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
				],
			},
			{
				field: "balance_sheet_statement.totalliabilities",
				name: "Total Liabilities",
				options: {
					notation: "compact",
					style: "currency",
					currency: "USD",
					maximumFractionDigits: 3,
				},
				children: [
					{
						field: "balance_sheet_statement.noninterestbearingdeposits",
						name: "Non-Interest Bearing Deposits",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "balance_sheet_statement.interestbearingdeposits",
						name: "Interest Bearing Deposits",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "balance_sheet_statement.fedfundspurchased",
						name: "Federal Funds Purchased and Securities Sold",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "balance_sheet_statement.shorttermdebt",
						name: "Short-Term Debt",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "balance_sheet_statement.longtermdebt",
						name: "Long-Term Debt",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "balance_sheet_statement.otherlongtermliabilities",
						name: "Other Long-Term Liabilities",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
				],
			},
			{
				field: "balance_sheet_statement.totalcommonequity",
				name: "Total Common Equity",
				options: {
					notation: "compact",
					style: "currency",
					currency: "USD",
					maximumFractionDigits: 3,
				},
				children: [
					{
						field: "balance_sheet_statement.commonequity",
						name: "Common Stock",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "balance_sheet_statement.retainedearnings",
						name: "Retained Earnings",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
				],
			},
			{
				field: "balance_sheet_statement.totalequityandnoncontrollinginterests",
				name: "Total Equity & Noncontrolling Interests",
				options: {
					notation: "compact",
					style: "currency",
					currency: "USD",
					maximumFractionDigits: 3,
				},
			},
			{
				field: "balance_sheet_statement.totalliabilitiesandequity",
				name: "Total Liabilities & Shareholders' Equity",
				options: {
					notation: "compact",
					style: "currency",
					currency: "USD",
					maximumFractionDigits: 3,
				},
			},
		],
	},

	{
		name: "Cash Flow",
		children: [
			{
				field: "cash_flow_statement.netcashfromoperatingactivities",
				name: "Net Cash From Operating Activities",
				options: {
					notation: "compact",
					style: "currency",
					currency: "USD",
					maximumFractionDigits: 3,
				},
				children: [
					{
						field: "income_statement.netincome",
						name: "Consolidated Net Income i (Loss)",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "income_statement.netincomediscontinued",
						name: "Net Income i (Loss) Discontinued Operations",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
				],
			},
			{
				field: "income_statement.netincomecontinuing",
				name: "Net Income / (Loss) Continuing Operatio",
				options: {
					notation: "compact",
					style: "currency",
					currency: "USD",
					maximumFractionDigits: 3,
				},
				children: [
					{
						field: "income_statement.netincomecontinuing",
						name: "Net Income / (Loss) Continuing Operatio",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "income_statement.depreciationexpense",
						name: "Depreciation Expense",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "income_statement.amortizationexpense",
						name: "Amortization Expense",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field:
							"cash_flow_statement.netcashfromcontinuingoperatingactivities",
						name: "Net Cash From Continuing Operating Activities",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field:
							"cash_flow_statement.netcashfromdiscontinuedoperatingactivities",
						name: "Net Cash From Discontinued Operating :",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
				],
			},
			{
				field: "cash_flow_statement.netcashfrominvestingactivities",
				name: "Net Cash From Investing Activities",
				options: {
					notation: "compact",
					style: "currency",
					currency: "USD",
					maximumFractionDigits: 3,
				},
				fncClassName,
				children: [
					{
						field: "cash_flow_statement.purchaseofplantpropertyandequipment",
						name: "Purchase of Property, Leasehold Improvements and Equipment",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
						fncClassName,
					},
					{
						field: "cash_flow_statement.acquisitions",
						name: "Acquisitions",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "cash_flow_statement.purchaseofinvestments",
						name: "Purchase of Investments",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
						fncClassName,
					},
					{
						field: "cash_flow_statement.saleofplantpropertyandequipment",
						name: "Sale of Property, Plant & Equipment",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "cash_flow_statement.divestitures",
						name: "Divestitures",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field:
							"cash_flow_statement.netcashfromcontinuinginvestingactivities",
						name: "Net Cash From Continuing Investing Act.",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
						fncClassName,
					},
					{
						field:
							"cash_flow_statement.netcashfromdiscontinuedinvestingactivities",
						name: "Net Cash From Discontinued Investing f",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "cash_flow_statement.increasedecreaseinoperatingcapital",
						name: "Changes in Operating Assets and Liabilities, net",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "cash_flow_statement.noncashadjustmentstonetincome",
						name: "Non-Cash Adjustments to Reconcile Net Income",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
				],
			},
			{
				field: "cash_flow_statement.netcashfromfinancingactivities",
				name: "Net Cash From Financing Activities",
				options: {
					notation: "compact",
					style: "currency",
					currency: "USD",
					maximumFractionDigits: 3,
				},
				fncClassName,
				children: [
					{
						field: "cash_flow_statement.repaymentofdebt",
						name: "Repayment of Debt",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "cash_flow_statement.repurchaseofpreferredequity",
						name: "Repurchase of Preferred Equity",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "cash_flow_statement.repurchaseofcommonequity",
						name: "Repurchase of Common Equity",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
						fncClassName,
					},
					{
						field: "cash_flow_statement.paymentofdividends",
						name: "Payment of Dividends",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
						fncClassName,
					},
					{
						field: "cash_flow_statement.issuanceofdebt",
						name: "Issuance of Debt",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "cash_flow_statement.issuanceofpreferredequity",
						name: "Issuance of Preferred Equity",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "cash_flow_statement.issuanceofcommonequity",
						name: "Issuance of Common Equity",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "cash_flow_statement.otherfinancingactivitiesnet",
						name: "Other Financing Activities, Net",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
						fncClassName,
					},
					{
						field:
							"cash_flow_statement.netcashfromcontinuingfinancingactivities",
						name: "Net Cash From Continuing Financing Activities",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
						fncClassName,
					},
					{
						field:
							"cash_flow_statement.netcashfromdiscontinuedfinancingactivities",
						name: "Net Cash From Discontinued Financing Activities",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
				],
			},
			{
				field: "calculations.fcffqoqgrowth",
				name: "Free Cash Flow Firm Q/Q Growth",
				options: { style: "percent" },
				fncClassName,
			},
			{
				field: "calculations.fcffgrowth",
				name: "Free Cash Flow Firm Growth",
				options: { style: "percent" },
				fncClassName,
			},
			{
				field: "cash_flow_statement.netchangeincash",
				name: "Net Change in Cash & Equivalents",
				options: {
					notation: "compact",
					style: "currency",
					currency: "USD",
					maximumFractionDigits: 3,
				},
				fncClassName,
				children: [
					{
						field: "cash_flow_statement.effectofexchangeratechanges",
						name: "Effect of Exchange Rate Changes",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "cash_flow_statement.othernetchangesincash",
						name: "Other Net Changes in Cash",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "cash_flow_statement.cashincometaxespaid",
						name: "Cash Income Taxes Paid",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "cash_flow_statement.cashinterestpaid",
						name: "Cash Interest Paid",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "cash_flow_statement.cashinterestreceived",
						name: "Cash Interest Received",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
				],
			},
		],
	},
];
